import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { Download, AlertTriangle, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { IRC_S6 } from "@/constants/testIds";

// Round 19 · IRC Form S6 · annual monthly reconciliation.
// Twelve monthly boxes (10..120) + TOTAL(1) box 130 + TOTAL(2) box 140.
// Reconcile-break banner fires when TOTAL(1) != TOTAL(2), naming the
// driving month. Both totals derive from the same DB in this design,
// so a break signals a future divergence source (see backend/irc_s6.py).

export default function IrcS6() {
    const [year, setYear] = useState(String(new Date().getFullYear()));
    const [env, setEnv] = useState(null);
    const [loading, setLoading] = useState(false);
    const years = useMemo(() => {
        const y = new Date().getFullYear();
        return Array.from({ length: 9 }, (_, i) => String(y - i + 2)).reverse();
    }, []);

    const load = async (y = year) => {
        setLoading(true);
        try {
            const { data } = await api.get(`/irc/s6/preview?year=${y}`);
            setEnv(data);
        } catch (ex) {
            toast.error(formatApiError(ex?.response?.data?.detail) || "Failed to load S6");
            setEnv(null);
        } finally { setLoading(false); }
    };

    useEffect(() => { load(year); /* eslint-disable-next-line */ }, [year]);

    const download = () => {
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/irc/s6.pdf?year=${year}`;
        const token = localStorage.getItem("mss_token");
        fetch(url, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => { if (!r.ok) throw new Error(r.status); return r.blob(); })
            .then((b) => {
                const a = document.createElement("a");
                a.href = URL.createObjectURL(b); a.download = `irc_s6_${year}.pdf`;
                document.body.appendChild(a); a.click(); a.remove();
                URL.revokeObjectURL(a.href);
            })
            .catch((e) => toast.error(`Download failed (${e.message})`));
    };

    return (
        <div data-testid={IRC_S6.page} className="p-10 max-w-[1000px]">
            <div className="mb-8 flex items-start justify-between gap-6">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.24em] text-neutral-500 font-display">
                        Reports · Internal Revenue Commission
                    </div>
                    <h1 className="font-display font-bold text-4xl mt-1 text-[color:var(--kp-charcoal)]">
                        Form S6 · Annual monthly reconciliation
                    </h1>
                    <p className="text-sm text-neutral-500 mt-2 leading-relaxed max-w-[720px]">
                        Twelve monthly SWT boxes plus two totals. TOTAL(1) = sum of monthly S2 box-50
                        values. TOTAL(2) = sum of S1 column 6 per employee. They should be identical —
                        both aggregations of the same source. If they aren't, the reconcile-break
                        banner fires.
                    </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <select data-testid={IRC_S6.yearPicker} value={year} onChange={(e) => setYear(e.target.value)}
                        className="kp-focus border border-neutral-300 px-3 py-2 text-[13px] bg-white rounded-[3px]">
                        {years.map((y) => <option key={y} value={y}>{y}</option>)}
                    </select>
                    <button data-testid={IRC_S6.downloadBtn} onClick={download} disabled={!env || loading}
                        className="inline-flex items-center gap-2 px-4 py-2 text-[12px] font-display font-semibold uppercase tracking-[0.12em] text-white rounded-[3px] disabled:opacity-40"
                        style={{ background: "var(--kp-red)" }}>
                        <Download size={12} /> Download PDF
                    </button>
                </div>
            </div>

            {env && env.reconcile_break && (
                <div data-testid={IRC_S6.reconcileBreakBanner}
                    className="mb-6 border-2 rounded-[3px] p-4"
                    style={{ borderColor: "var(--kp-red)", background: "rgba(227,6,19,0.08)", color: "#8B0F17" }}>
                    <div className="font-display font-bold uppercase tracking-[0.14em] text-[13px]">
                        <AlertTriangle size={14} className="inline mr-1" />
                        Reconcile break · TOTAL(1) ≠ TOTAL(2)
                    </div>
                    <div className="mt-1 text-[12px]">
                        Delta <strong className="font-mono-money">{env.reconcile_break.delta_kina}</strong> —
                        largest per-month drift at <strong>{env.reconcile_break.driver_month}</strong>
                        (<span className="font-mono-money">{env.reconcile_break.driver_delta_kina}</span>).
                    </div>
                </div>
            )}

            {env && env.reconciles && env.box_130_total_1_toea > 0 && (
                <div data-testid={IRC_S6.reconcileOkBadge}
                    className="mb-6 inline-flex items-center gap-2 px-3 py-1.5 text-[11px] font-display font-semibold uppercase tracking-[0.14em] rounded-[2px]"
                    style={{ background: "#E5F3EA", color: "var(--kp-success)" }}>
                    <CheckCircle2 size={12} /> Totals reconcile
                </div>
            )}

            {env && (
                <div className="border border-neutral-200 rounded-[3px] bg-white overflow-hidden">
                    <table className="w-full text-[13px]">
                        <thead>
                            <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-neutral-500 font-display" style={{ background: "#FAFAFA" }}>
                                <th className="px-4 py-2 w-[70px] text-center">Box</th>
                                <th className="px-4 py-2">Month</th>
                                <th className="px-4 py-2 text-right">SWT</th>
                            </tr>
                        </thead>
                        <tbody>
                            {env.monthly_boxes.map((mb) => (
                                <tr key={mb.month} data-testid={IRC_S6.monthlyBox(mb.box_number)} className="border-t border-neutral-100">
                                    <td className="px-4 py-2 text-center font-mono-money font-semibold">{mb.box_number}</td>
                                    <td className="px-4 py-2 text-[color:var(--kp-charcoal)]">
                                        {mb.month_name} <span className="text-neutral-400 font-mono-money text-[10px] ml-1">{mb.month}</span>
                                        {mb.lodged ? (
                                            <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-display font-semibold uppercase tracking-[0.14em] rounded-[2px]"
                                                style={{ background: "var(--kp-charcoal)", color: "white" }}>
                                                Lodged
                                            </span>
                                        ) : mb.swt_toea > 0 ? (
                                            <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-display font-semibold uppercase tracking-[0.14em] rounded-[2px]"
                                                style={{ background: "#FFF3D6", color: "#B57800" }}>
                                                Not lodged
                                            </span>
                                        ) : null}
                                        {mb.lodged && mb.drift_toea !== 0 && (
                                            <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-display font-semibold uppercase tracking-[0.14em] rounded-[2px]"
                                                style={{ background: "rgba(227,6,19,0.15)", color: "var(--kp-red)" }}>
                                                Drift · live {mb.live_swt_kina}
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-4 py-2 text-right font-mono-money">{mb.swt_kina}</td>
                                </tr>
                            ))}
                            <tr className="border-t-2 border-neutral-800">
                                <td className="px-4 py-3 text-center font-mono-money font-bold">130</td>
                                <td className="px-4 py-3 font-display font-semibold">TOTAL(1) · sum of monthly SWT (10..120)</td>
                                <td data-testid={IRC_S6.total1} className="px-4 py-3 text-right font-mono-money font-bold">{env.box_130_total_1_kina}</td>
                            </tr>
                            <tr>
                                <td className="px-4 py-3 text-center font-mono-money font-bold">140</td>
                                <td className="px-4 py-3 font-display font-semibold">TOTAL(2) · sum of S1 col 6 per employee</td>
                                <td data-testid={IRC_S6.total2} className="px-4 py-3 text-right font-mono-money font-bold" style={{ color: env.reconciles ? "var(--kp-charcoal)" : "var(--kp-red)" }}>
                                    {env.box_140_total_2_kina}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
